import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import "./DimensionSummary.css";
import DemousdzViewer from "./DemousdzViewer";
import {
  ApiBaseUrl,
  getJobDetailsWithBidProfessional,
} from "../../../../Utils/Constants";

/*
NOTE: For robust USDZ error handling, ensure the demousdzapp iframe viewer
(public/demousdzapp/index.html) includes a message listener that calls your
USDZ parsing (e.g. parseAndRenderUSDZ) and posts DEMO_USDZ_STATUS success/error
back to the parent so the React UI can surface memory/format issues.
*/

const SERVICE_SURFACE_TYPE_MAP = {
  floorcoverings: "wall",
  garagedoor: "door",
  doorswindows: "door",
  doors: "door",
  windows: "window",
  artificialgrass: "side",
  bath: "wall",
  interiorpaint: "wall",
  kitchen: "wall",
};

const parseJsonSafely = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const normalizeServiceKey = (value = "") =>
  String(value).replace(/[^a-z]/gi, "").toLowerCase();

const getAbsoluteMediaUrl = (url) => {
  if (!url || typeof url !== "string") {
    return "";
  }

  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return "";
  }

  if (/^(https?:)?\/\//i.test(trimmedUrl) || trimmedUrl.startsWith("data:")) {
    return trimmedUrl;
  }

  const normalizedBaseUrl = ApiBaseUrl.endsWith("/")
    ? ApiBaseUrl.slice(0, -1)
    : ApiBaseUrl;
  const normalizedPath = trimmedUrl.startsWith("/")
    ? trimmedUrl
    : `/${trimmedUrl}`;

  return `${normalizedBaseUrl}${normalizedPath}`;
};

const getServiceSurfaceType = (jobDetailsResponse) => {
  const serviceCandidates = [
    jobDetailsResponse?.serviceType,
    jobDetailsResponse?.serviceName,
    jobDetailsResponse?.serviceId?.serviceType,
    jobDetailsResponse?.serviceId?.name,
  ];

  for (const candidate of serviceCandidates) {
    const normalizedCandidate = normalizeServiceKey(candidate);

    if (SERVICE_SURFACE_TYPE_MAP[normalizedCandidate]) {
      return SERVICE_SURFACE_TYPE_MAP[normalizedCandidate];
    }
  }

  return null;
};

const getMeasurementUsdzUrl = (measurement) => {
  const directCandidates = [
    measurement?.usdz,
    measurement?.usdzUrl,
    measurement?.usdzURL,
    measurement?.usdZUrl,
    measurement?.url,
  ];

  for (const candidate of directCandidates) {
    if (
      typeof candidate === "string" &&
      candidate.trim() &&
      candidate.trim().toLowerCase().includes(".usdz")
    ) {
      return getAbsoluteMediaUrl(candidate);
    }
  }

  const mediaCandidates = toArray(measurement?.mediaItems);

  const matchedMedia = mediaCandidates.find(
    (candidate) =>
      typeof candidate === "string" && candidate.toLowerCase().includes(".usdz")
  );

  return getAbsoluteMediaUrl(matchedMedia);
};

const getMeasurementModelUrl = (measurement) => {
  const directCandidates = [
    measurement?.glb,
    measurement?.glbUrl,
    measurement?.gltf,
    measurement?.gltfUrl,
    measurement?.model,
    measurement?.modelUrl,
    measurement?.threeDModelUrl,
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return getAbsoluteMediaUrl(candidate);
    }
  }

  const mediaCandidates = toArray(measurement?.mediaItems);

  const matchedMedia = mediaCandidates.find(
    (candidate) =>
      typeof candidate === "string" &&
      (candidate.toLowerCase().includes(".glb") ||
        candidate.toLowerCase().includes(".gltf"))
  );

  return getAbsoluteMediaUrl(matchedMedia);
};

const getMeasurementPosterUrl = (measurement) => {
  const mediaCandidates = toArray(measurement?.mediaItems);

  const matchedMedia = mediaCandidates.find(
    (candidate) =>
      typeof candidate === "string" &&
      !candidate.toLowerCase().includes(".usdz") &&
      !candidate.toLowerCase().includes(".glb") &&
      !candidate.toLowerCase().includes(".gltf")
  );

  return getAbsoluteMediaUrl(matchedMedia);
};

const meterToFeetInches = (meters) => {
  if (Number(meters || 0) <= 0.01) {
    return "";
  }

  const feetValue = Number(meters || 0) * 3.28084;

  let feet = Math.floor(feetValue);
  let inches = Math.round((feetValue - feet) * 12);

  if (inches === 12) {
    feet++;
    inches = 0;
  }

  return `${feet}'${inches}" `;
};

const getRoomAreaValue = (room) => {
  const areaSqft = Number(
    room?.areaSqft ??
      room?.areaSqFt ??
      room?.squareFeet ??
      room?.squareFootage ??
      room?.area ??
      0
  );

  if (areaSqft > 0) {
    return areaSqft.toFixed(2);
  }

  const areaSqm = Number(room?.areaSqm || 0);

  if (areaSqm > 0) {
    return (areaSqm * 10.7639).toFixed(2);
  }

  return "0.00";
};

const getRoomAreaSqm = (room) => {
  const areaSqm = Number(room?.areaSqm || 0);

  if (areaSqm > 0) {
    return areaSqm;
  }

  const areaSqft = Number(
    room?.areaSqft ??
      room?.areaSqFt ??
      room?.squareFeet ??
      room?.squareFootage ??
      room?.area ??
      0
  );

  if (areaSqft > 0) {
    return areaSqft / 10.7639;
  }

  return 0;
};

/* IMPORTANT:
Keep original coordinates for design/arc shape.
Do NOT normalize coordinates.
*/
const getWallPoint = (point) => ({
  x: Number(point?.x || 0),
  y: Number(point?.z ?? point?.y ?? 0),
});

const WALL_POINT_TOLERANCE = 0.25;

const getWallPointDistance = (firstPoint, secondPoint) => {
  if (!firstPoint || !secondPoint) {
    return Number.POSITIVE_INFINITY;
  }

  return Math.hypot(firstPoint.x - secondPoint.x, firstPoint.y - secondPoint.y);
};

const areWallPointsConnected = (firstPoint, secondPoint) => {
  if (!firstPoint || !secondPoint) {
    return false;
  }

  return getWallPointDistance(firstPoint, secondPoint) <= WALL_POINT_TOLERANCE;
};

const calculateWallLength = (start, end) => {
  return Math.hypot(end.x - start.x, end.y - start.y);
};

const isArcWall = (wall) => {
  return Boolean(
    wall?.isArc ||
      wall?.arc ||
      wall?.curve ||
      wall?.curved ||
      wall?.radius ||
      wall?.sweepAngle ||
      wall?.startAngle ||
      wall?.endAngle ||
      wall?.centerPoint ||
      wall?.arcLength
  );
};

const getPolygonArea = (points) => {
  if (points.length < 3) {
    return 0;
  }

  let area = 0;

  for (let index = 0; index < points.length; index += 1) {
    const currentPoint = points[index];
    const nextPoint = points[(index + 1) % points.length];

    area += currentPoint.x * nextPoint.y - nextPoint.x * currentPoint.y;
  }

  return Math.abs(area) / 2;
};

const getRoomScaleFactor = (walls, room) => {
  const normalizedWalls = toArray(parseJsonSafely(walls));
  const points = normalizedWalls
    .map((wall) => getWallPoint(wall?.start || wall?.planStartPoint))
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));

  const polygonArea = getPolygonArea(points);
  const actualAreaSqm = getRoomAreaSqm(room);

  if (polygonArea <= 0 || actualAreaSqm <= 0) {
    return null;
  }

  const derivedScale = Math.sqrt(polygonArea / actualAreaSqm);

  return Number.isFinite(derivedScale) && derivedScale > 1
    ? derivedScale
    : null;
};

const normalizeLengthValue = (value, scaleFactor, useApiAsMeters) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return 0;

  if (useApiAsMeters) {
    return n;
  }

  if (scaleFactor && scaleFactor > 1) {
    return n / scaleFactor;
  }

  return n;
};
const resolveWallLength = (
  wall,
  chordLength,
  apiLength,
  scaleFactor,
  useApiAsMeters
) => {
  const fixedApiLength = normalizeLengthValue(
    apiLength,
    scaleFactor,
    useApiAsMeters
  );
  const fixedChordLength = normalizeLengthValue(
    chordLength,
    scaleFactor,
    false
  );

  if (useApiAsMeters && fixedApiLength > 0) {
    return fixedApiLength;
  }

  if (isArcWall(wall)) {
    if (fixedApiLength > 0) return fixedApiLength;
    if (fixedChordLength > 0) return fixedChordLength;
  }

  if (fixedApiLength > 0 && fixedApiLength > fixedChordLength + 0.01) {
    return fixedApiLength;
  }

  if (fixedChordLength > 0) {
    return fixedChordLength;
  }

  return fixedApiLength;
};

const normalizeWalls = (walls, options = {}) => {
  const { scaleFactor = null, useApiAsMeters = false } = options;

  return toArray(parseJsonSafely(walls))
    .map((wall, index) => {
      const start = getWallPoint(wall?.start || wall?.planStartPoint);
      const end = getWallPoint(wall?.end || wall?.planEndPoint);

      const calcLength = calculateWallLength(start, end);

      /* Keep this raw */
      const apiLength = Number(
        wall?.length ?? wall?.width ?? wall?.distance ?? 0
      );

      const finalLength = resolveWallLength(
        wall,
        calcLength,
        apiLength,
        scaleFactor,
        useApiAsMeters
      );

      return {
        id: index + 1,
        name: wall?.nodeName || `Wall ${index + 1}`,
        width: meterToFeetInches(finalLength),
        height: meterToFeetInches(Number(wall?.height || 0)),
        rawLength: finalLength,
        isArc: isArcWall(wall),
        start,
        end,
      };
    })
    .filter((wall) => wall.name);
};

const normalizeAddons = (addons) => {
  return toArray(parseJsonSafely(addons))
    .map((addon, index) => ({
      id: addon?.id || addon?._id || `addon-${index + 1}`,
      name:
        addon?.name ||
        addon?.nodeName ||
        addon?.updatedName ||
        `Add-on ${index + 1}`,
      isOn: Boolean(addon?.isOn ?? addon?.isVisible),
    }))
    .filter((addon) => addon.name && addon.isOn);
};

const getRoomAreaName = (room, index) => {
  return room?.name || room?.nodeName || room?.updatedName || `Area ${index + 1}`;
};

const getDisplaySurfaces = (surfaces, preferredType) => {
  const normalizedSurfaces = toArray(parseJsonSafely(surfaces));

  if (!preferredType) {
    return normalizedSurfaces;
  }

  const matchingSurfaces = normalizedSurfaces.filter(
    (surface) => normalizeServiceKey(surface?.type) === preferredType
  );

  return matchingSurfaces.length ? matchingSurfaces : normalizedSurfaces;
};

const normalizeSurfaceMeasurements = (source, preferredType) => {
  const parsed = parseJsonSafely(source);

  return toArray(parsed).map((measurement, index) => ({
    name: getRoomAreaName(measurement, index),
    roomArea: getRoomAreaValue(measurement),
    usdzUrl: getMeasurementUsdzUrl(measurement),
    modelUrl: getMeasurementModelUrl(measurement),
    posterUrl: getMeasurementPosterUrl(measurement),
    walls: normalizeWalls(
      getDisplaySurfaces(measurement?.surfaces, preferredType),
      { useApiAsMeters: true }
    ),
    addons: normalizeAddons(measurement?.addons),
  }));
};

const normalizeLegacyMeasurements = (source) => {
  const parsed = parseJsonSafely(source);

  if (Array.isArray(parsed)) {
    return parsed.flatMap((room, index) => {
      const resolved = parseJsonSafely(
        room?.measurement || room?.room || room?.data || room
      );

      const roomList = Array.isArray(resolved?.rooms)
        ? resolved.rooms
        : [resolved];

      return roomList.map((item, innerIndex) => ({
        name: getRoomAreaName(item, index + innerIndex),
        roomArea: getRoomAreaValue(item),
        walls: normalizeWalls(item?.walls, {
          scaleFactor: getRoomScaleFactor(item?.walls, item),
        }),
        addons: normalizeAddons(item?.addons),
      }));
    });
  }

  if (parsed?.rooms) {
    return normalizeLegacyMeasurements(parsed.rooms);
  }

  return [];
};

/* KEEP ORIGINAL ARC/POLYGON DESIGN */
const orderConnectedWalls = (walls) => {
  const remainingWalls = walls.map((wall) => ({
    ...wall,
    start: { ...wall.start },
    end: { ...wall.end },
  }));

  if (remainingWalls.length <= 1) {
    return remainingWalls;
  }

  remainingWalls.sort((firstWall, secondWall) => {
    if (firstWall.start.x !== secondWall.start.x) {
      return firstWall.start.x - secondWall.start.x;
    }

    return firstWall.start.y - secondWall.start.y;
  });

  const orderedWalls = [remainingWalls.shift()];

  while (remainingWalls.length) {
    const lastWall = orderedWalls[orderedWalls.length - 1];
    let nextWallIndex = -1;
    let nextWallShouldFlip = false;
    let bestDistance = Number.POSITIVE_INFINITY;

    remainingWalls.forEach((candidate, index) => {
      const startDistance = getWallPointDistance(lastWall.end, candidate.start);
      const endDistance = getWallPointDistance(lastWall.end, candidate.end);
      const candidateDistance = Math.min(startDistance, endDistance);

      if (candidateDistance < bestDistance) {
        bestDistance = candidateDistance;
        nextWallIndex = index;
        nextWallShouldFlip = endDistance < startDistance;
      }
    });

    if (nextWallIndex === -1) {
      break;
    }

    const [nextWall] = remainingWalls.splice(nextWallIndex, 1);

    if (nextWallShouldFlip) {
      orderedWalls.push({
        ...nextWall,
        start: nextWall.end,
        end: nextWall.start,
      });
      continue;
    }

    orderedWalls.push(nextWall);
  }

  if (remainingWalls.length) {
    orderedWalls.push(...remainingWalls);
  }

  return orderedWalls;
};

const orderedPolygonPoints = (walls) => {
  const orderedWalls = orderConnectedWalls(walls);

  if (!orderedWalls.length) {
    return {
      orderedWalls: [],
      points: [],
    };
  }

  const points = orderedWalls.map((wall) => ({
    x: wall.start.x,
    y: wall.start.y,
  }));

  const lastWall = orderedWalls[orderedWalls.length - 1];

  points.push({
    x: lastWall.end.x,
    y: lastWall.end.y,
  });

  return {
    orderedWalls,
    points,
  };
};

const buildSvgData = (walls) => {
  const { orderedWalls, points } = orderedPolygonPoints(walls);

  if (!points.length) return null;

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const padding = 40;
  const width = 360;
  const height = 260;

  const scale = Math.min(
    (width - padding * 2) / (maxX - minX || 1),
    (height - padding * 2) / (maxY - minY || 1)
  );

  const contentWidth = (maxX - minX) * scale;
  const contentHeight = (maxY - minY) * scale;

  const offsetX = (width - contentWidth) / 2;
  const offsetY = (height - contentHeight) / 2;

  const mappedPoints = points.map((point) => ({
    x: offsetX + (point.x - minX) * scale,
    y: offsetY + (point.y - minY) * scale,
  }));

  const mappedWalls = orderedWalls.map((wall, index) => ({
    ...wall,
    mappedStart: mappedPoints[index],
    mappedEnd: mappedPoints[index + 1],
  }));

  const labelPlacements = [];

  const mappedWallsWithLabels = mappedWalls.map((wall) => {
    const pointOne = wall.mappedStart;
    const pointTwo = wall.mappedEnd;
    const midX = (pointOne.x + pointTwo.x) / 2;
    const midY = (pointOne.y + pointTwo.y) / 2;
    const dx = pointTwo.x - pointOne.x;
    const dy = pointTwo.y - pointOne.y;
    const wallLength = Math.hypot(dx, dy) || 1;
    const normalX = -dy / wallLength;
    const normalY = dx / wallLength;
    const centerVectorX = midX - width / 2;
    const centerVectorY = midY - height / 2;
    const outwardDot = normalX * centerVectorX + normalY * centerVectorY;
    const outwardNormalX = outwardDot >= 0 ? normalX : -normalX;
    const outwardNormalY = outwardDot >= 0 ? normalY : -normalY;

    let labelX = midX + outwardNormalX * 18;
    let labelY = midY + outwardNormalY * 18;

    for (let attempt = 0; attempt < 6; attempt += 1) {
      const currentLabelX = labelX;
      const currentLabelY = labelY;
      const overlapsExistingLabel = labelPlacements.some(
        (placement) =>
          Math.abs(placement.x - currentLabelX) < 34 &&
          Math.abs(placement.y - currentLabelY) < 18
      );

      if (!overlapsExistingLabel) {
        break;
      }

      labelX += outwardNormalX * 12;
      labelY += outwardNormalY * 12;
    }

    labelPlacements.push({ x: labelX, y: labelY });

    return {
      ...wall,
      labelX,
      labelY,
    };
  });

  return {
    width,
    height,
    polygonPoints: mappedPoints
      .map((point) => `${point.x},${point.y}`)
      .join(" "),
    mappedPoints,
    mappedWalls: mappedWallsWithLabels,
    center: {
      x: width / 2,
      y: height / 2,
    },
  };
};

const getDisplayRoomName = (name = "") => {
  const trimmed = String(name).trim();

  if (trimmed.length <= 18) return trimmed;

  return `${trimmed.slice(0,18)}...`;
};

export default function DimensionSummary({
  jobId,
  initialJobData
}) {
  const [view, setView] = useState("2D");
  const [zoomModal, setZoomModal] = useState(false);
  const [showAllWalls, setShowAllWalls] = useState(false);

  const [jobDetailsResponse, setJobDetailsResponse] =
    useState(initialJobData || null);

  const [activeRoomIndex, setActiveRoomIndex] =
    useState(0);

  useEffect(() => {

    if (!jobId) {
      alert("Issue: Job ID not found");
      return;
    }

    const fetchJobDetails = async () => {
      try {

        const url =
          `${ApiBaseUrl}${getJobDetailsWithBidProfessional}`;

        const response =
          await axios.get(url,{
            headers:{
              Authorization:
                localStorage.getItem(
                  "professionalToken"
                )
            },
            params:{ jobId }
          });

        if (response.data?.data) {
          setJobDetailsResponse(
            response.data.data
          );
        }

      } catch(error){
        console.log(error);
      }
    };

    fetchJobDetails();

  },[jobId]);

  const preferredSurfaceType =
    useMemo(
      () => getServiceSurfaceType(jobDetailsResponse),
      [jobDetailsResponse]
    );
  const rooms =
    useMemo(() => {
      const surfaceMeasurements =
        jobDetailsResponse?.surfaceMeasurements;

      if (toArray(surfaceMeasurements).length > 0) {
        return normalizeSurfaceMeasurements(
          surfaceMeasurements,
          preferredSurfaceType
        );
      }

      return normalizeLegacyMeasurements(
        jobDetailsResponse?.measurements
      );
    },[jobDetailsResponse, preferredSurfaceType]);

  const room =
    rooms[activeRoomIndex] || rooms[0];

  const walls =
    room?.walls || [];

  const addons =
    room?.addons || [];

  const currentUsdzUrl =
    room?.usdzUrl || "";

  const currentModelUrl =
    room?.modelUrl || "";

  const currentPosterUrl =
    room?.posterUrl || "";

  const visibleWalls =
    showAllWalls
      ? walls
      : walls.slice(0,6);

  const surfaceLabel =
    preferredSurfaceType
      ? `${preferredSurfaceType.charAt(0).toUpperCase()}${preferredSurfaceType.slice(1)}s`
      : "Walls";

  const svgData =
    useMemo(
      () => buildSvgData(walls),
      [walls]
    );

  if (!room) {
    return <div>Loading...</div>;
  }

  const ThreeDViewBox = () => {
    return (
      <DemousdzViewer
        usdzUrl={currentUsdzUrl}
        modelUrl={currentModelUrl}
        posterUrl={currentPosterUrl}
        roomName={room?.name}
      />
    );
  };

  const FloorPlanBox = () => (
    <div className="floor-plan-svg-wrap">

      {svgData && (
        <svg
          className="room-svg"
          viewBox={`0 0 ${svgData.width} ${svgData.height}`}
        >

          <polygon
            points={svgData.polygonPoints}
            className="room-polygon"
          />

          {svgData.mappedWalls.map((wall) => {
            const pointOne = wall.mappedStart;
            const pointTwo = wall.mappedEnd;

            if(!pointOne || !pointTwo)
              return null;

            return(
              <g key={wall.id}>

                <line
                  x1={pointOne.x}
                  y1={pointOne.y}
                  x2={pointTwo.x}
                  y2={pointTwo.y}
                  className={`room-wall-line ${wall.isArc ? "room-wall-arc" : ""}`}
                />

                {wall.width && (
                  <text
                    x={wall.labelX}
                    y={wall.labelY}
                    textAnchor="middle"
                    className="svg-wall-label"
                  >
                    {wall.width}
                  </text>
                )}

              </g>
            );

          })}

          <text
            x={svgData.center.x}
            y={svgData.center.y-4}
            textAnchor="middle"
            className="svg-room-name"
          >
            {getDisplayRoomName(room.name)}
          </text>

          <text
            x={svgData.center.x}
            y={svgData.center.y+20}
            textAnchor="middle"
            className="svg-room-area"
          >
            {room.roomArea} ft²
          </text>

        </svg>
      )}

      <div className="dimension-controls">
        <button className="control-btn">
          H: {walls[0]?.height}
        </button>

        <button className="control-btn">
          {room.roomArea} ft²
        </button>
      </div>

    </div>
  );

  return (
    <>
      <div className="dimension-summary-shell">

        <div className="dimension-summary-header">
          <div>
            <p className="dimension-summary-eyebrow">
              Measured layout
            </p>

            <h2 className="dimension-summary-title">
              Dimension Summary
            </h2>
          </div>

          <div className="dimension-summary-badges">
            <span className="summary-badge">
              {room?.name}
            </span>

            <span className="summary-badge summary-badge-strong">
              {room?.roomArea} ft²
            </span>
          </div>
        </div>

        <div className="view-toggle">
          <button
            className={view==="2D"?"active":""}
            onClick={()=>setView("2D")}
          >
            2D
          </button>

          <button
            className={view==="3D"?"active":""}
            onClick={()=>setView("3D")}
          >
            3D
          </button>
        </div>

        {rooms.length > 1 && (
          <div className="room-slider">

            <button
              onClick={() =>
                setActiveRoomIndex(prev =>
                  Math.max(prev-1,0)
                )
              }
              className="room-nav-btn"
            >
              <ChevronLeft size={18}/>
            </button>

            <div className="room-tabs">
              {rooms.map((item,index)=>(
                <button
                  key={index}
                  className={
                    activeRoomIndex===index
                      ?"active":""
                  }
                  onClick={()=>
                    setActiveRoomIndex(index)
                  }
                >
                  {item.name}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setActiveRoomIndex(prev =>
                  Math.min(
                    prev+1,
                    rooms.length-1
                  )
                )
              }
              className="room-nav-btn"
            >
              <ChevronRight size={18}/>
            </button>

          </div>
        )}

        <div className="floor-plan-designer">

          <div className="canvas-area">

            <button
              className="zoom-btn canvas-zoom-btn"
              onClick={() =>
                setZoomModal(true)
              }
            >
              <span className="zoom-btn-icon">
                <Maximize2 size={16}/>
              </span>

              <span className="zoom-btn-text">
                Expand
              </span>
            </button>

            {view === "2D" ? (
              <FloorPlanBox/>
            ) : (
              <ThreeDViewBox/>
            )}

          </div>

          <div className="dimensions-summary">

            <h3>Dimensions Summary</h3>

            <table>
              <thead>
                <tr>
                  <th>{surfaceLabel}</th>
                  <th>Width</th>
                  <th>Height</th>
                </tr>
              </thead>

              <tbody>
                {visibleWalls.map(wall=>(
                  <tr key={wall.id}>
                    <td>{wall.name}</td>
                    <td>{wall.width}</td>
                    <td>{wall.height}</td>
                  </tr>
                ))}
              </tbody>

            </table>

            {walls.length > 6 && (
              <div className="dimensions-summary-footer">
                <button
                  className="load-more-btn"
                  onClick={() => setShowAllWalls((prev) => !prev)}
                >
                  {showAllWalls
                    ? "Show Less"
                    : `Load More (${walls.length - 6})`}
                </button>
              </div>
            )}

            {addons.length > 0 && (
              <div className="dimension-addons">
                <h4>Add-ons</h4>

                <div className="dimension-addon-list">
                  {addons.map((addon) => (
                    <div
                      key={addon.id}
                      className="dimension-addon-item"
                    >
                      <span>{addon.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

        </div>

      </div>

      {zoomModal && (
        <div
          className="plan-modal-overlay"
          onClick={() =>
            setZoomModal(false)
          }
        >
          <div
            className="plan-modal"
            onClick={(e)=>
              e.stopPropagation()
            }
          >
            <button
              className="plan-modal-close"
              onClick={() =>
                setZoomModal(false)
              }
            >
              ×
            </button>

            {view === "2D" ? (
              <FloorPlanBox/>
            ) : (
              <ThreeDViewBox/>
            )}

          </div>
        </div>
      )}

    </>
  );
}
